import React, { useState } from "react";
import { Card, Form, Row, Col, Button, Container, InputGroup } from "react-bootstrap";
import { FaMoneyBillWave, FaPercentage, FaCalendarAlt } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const LoanCalculator = () => {
  const [loanType, setLoanType] = useState("Personal Loan");
  const [amount, setAmount] = useState(500000);
  const [interest, setInterest] = useState(12);
  const [years, setYears] = useState(5);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);
  const [schedule, setSchedule] = useState([]);
  const [showModal, setShowModal] = useState(false);

  // Loan types and their respective service charges and interest rates
  const loanTypes = {
    "ASSET FINANCE": {
      interestRate: 12,
      serviceCharge: "Maximum loan is 80% of the asset value. Maximum repayment period is 60 months. Security of the loan is the asset.",
      processingFee: 0.015
    },
    "DEVELOPMENT LOAN": {
      interestRate: 12,
      serviceCharge: "Maximum loan is 3 times members deposit. Maximum repayment period of 48 months. Guarantors or collateral can be used as security.",
      processingFee: 0.015
    },
    "DIVIDEND ADVANCE": {
      interestRate: 0,
      serviceCharge: "This loan is provided based on the member's dividend at zero interest.",
      processingFee: 0
    },
    "EMERGENCY LOAN": {
      interestRate: 15,
      serviceCharge: "Maximum amount is 100,000. Maximum repayment period is 12 months.",
      processingFee: 0.015
    },
    "INSTANT LOAN": {
      interestRate: 4.5,
      serviceCharge: "Maximum loan is 30,000. Maximum repayment period is 4 months. Application available online.",
      processingFee: 0
    },
    "INVESTMENT LOAN": {
      interestRate: 14,
      serviceCharge: "Maximum loan is 3 times member’s deposit. Maximum repayment period of 48 months. Processing fee of 1.5%.",
      processingFee: 0.015
    },
    "SCHOOL FEES LOAN": {
      interestRate: 12,
      serviceCharge: "Maximum amount is 200,000. Maximum repayment period is 24 months.",
      processingFee: 0.015
    },
    "PLOT PURCHASE LOAN": {
      interestRate: 14,
      serviceCharge: "Loan provided for purchasing plots. Maximum loan is 3 times the deposit. Processing fee of 2%.",
      processingFee: 0.02
    }
  };

  const handleLoanTypeChange = (e) => {
    const selectedType = e.target.value;
    setLoanType(selectedType);
    setInterest(loanTypes[selectedType].interestRate);
    
    // Show modal only for loans with service charge
    if (loanTypes[selectedType].processingFee > 0) {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  };

  const handleCloseModal = () => setShowModal(false);

  const calculateLoan = () => {
    const principal = amount;
    const processingFee = loanTypes[loanType].processingFee;
    const feeAmount = principal * processingFee;
    const totalLoanAmount = principal + feeAmount; // Adding processing fee to the total loan

    const calculatedInterest = interest / 100 / 12;
    const totalMonths = years * 12;
    const x = Math.pow(1 + calculatedInterest, totalMonths);
    const monthly = (totalLoanAmount * x * calculatedInterest) / (x - 1);

    const totalPay = monthly * totalMonths;
    const totalInt = totalPay - totalLoanAmount;

    setMonthlyPayment(monthly.toFixed(2));
    setTotalPayment(totalPay.toFixed(2));
    setTotalInterest(totalInt.toFixed(2));

    let balance = totalLoanAmount;
    let scheduleArr = [];
    for (let i = 1; i <= totalMonths; i++) {
      let interestPaid = balance * calculatedInterest;
      let principalPaid = monthly - interestPaid;
      balance -= principalPaid;
      scheduleArr.push({ month: i, principalPaid, interestPaid, balance });
    }
    setSchedule(scheduleArr);
  };

  return (
    <>
      <Navbar />
      <div className="loan-container">
        <div className="phone-frame">
          <div className="phone-screen">
            <Container>
              <Card className="loan-card">
                <h2 className="text-center loan-heading">💰 Loan Calculator</h2>
                <Form className="loan-form">
                  <Row className="mb-2">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Loan Type</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaPercentage /></InputGroup.Text>
                          <Form.Control
                            as="select"
                            value={loanType}
                            onChange={handleLoanTypeChange}
                            className="form-select-visible"
                          >
                            <option value="">Select Loan Type</option>
                            {Object.keys(loanTypes).map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </Form.Control>
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Interest Rate (%)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaPercentage /></InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={interest}
                            onChange={(e) => setInterest(e.target.value)}
                            className="form-input-visible"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Loan Amount (KES)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaMoneyBillWave /></InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="form-input-visible"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>

                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Loan Term (Years)</Form.Label>
                        <InputGroup>
                          <InputGroup.Text><FaCalendarAlt /></InputGroup.Text>
                          <Form.Control
                            type="number"
                            value={years}
                            onChange={(e) => setYears(e.target.value)}
                            className="form-input-visible"
                          />
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button variant="primary" onClick={calculateLoan} className="w-100 fancy-button">
                    🧮 Calculate
                  </Button>
                </Form>

                {monthlyPayment > 0 && (
                  <div className="mt-3 text-center loan-results">
                    <h5>📊 Monthly: <strong>KES {monthlyPayment}</strong></h5>
                    <h6>💰 Total Interest: <strong>KES {totalInterest}</strong></h6>
                    <h6>🛒 Total Payment: <strong>KES {totalPayment}</strong></h6>
                  </div>
                )}
              </Card>
            </Container>
          </div>
        </div>
      </div>

      {/* Custom Cool Card-style Pop-up */}
      {showModal && (
        <div className="custom-modal">
          <div className="modal-card">
            <div className="modal-header">
              <h4 className="modal-title">Service Charge Details</h4>
              <button className="close-btn" onClick={handleCloseModal}>X</button>
            </div>
            <div className="modal-body">
              <h5>{loanType}</h5>
              <p><strong>Interest Rate:</strong> {loanTypes[loanType].interestRate}%</p>
              <p>{loanTypes[loanType].serviceCharge}</p>
              <hr />
            </div>
          </div>
        </div>
      )}

      <Footer />

      <style>
        {`
          .loan-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: calc(100vh - 100px);
            padding: 20px 0;
            background: #f8f9fa;
            overflow-x: hidden;
            margin-top: 30px;
          }

          .form-select-visible,
          .form-input-visible {
            background-color: #f0f0f0 !important;
            border: 1px solid #ccc;
            color: #333;
            padding: 10px;
            cursor: pointer;
            transition: all 0.3s ease;
            width: 100%;
          }

          .form-select-visible:focus,
          .form-input-visible:focus {
            outline: none;
            border-color: #007bff;
            background-color: #e0e0e0;
          }

          .phone-frame {
            width: 100%;
            max-width: 800px;
            height: 600px;
            background: #000;
            border-radius: 25px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
            display: flex;
            justify-content: center;
            align-items: center;
            position: relative;
            margin-top: 70px;
            margin-bottom: 0;
            padding: 5px;
            border: 2px solid #ccc;
          }

          .phone-frame .phone-screen {
            width: 92%;
            height: 95%;
            background: white;
            border-radius: 20px;
            overflow-y: auto;
            padding: 10px;
            box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.1);
          }

          .loan-form {
            padding-bottom: 0;
          }

          .fancy-button {
            background: linear-gradient(135deg, #007bff, #0056b3);
            border: none;
            padding: 8px;
            border-radius: 10px;
            font-weight: bold;
            transition: all 0.3s ease-in-out;
            width: 100%;
            margin-bottom: 0;
          }

          .loan-card {
            border-radius: 12px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            border: none;
            padding: 15px;
            background: white;
          }

          .loan-results {
            background: rgba(248, 249, 250, 0.8);
            padding: 8px;
            border-radius: 8px;
          }

          .modal-card {
            background-color: white;
            border-radius: 10px;
            padding: 20px;
            max-width: 400px;
            margin: 15% auto;
            box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
          }

          .custom-modal {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.4);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modal-title {
            font-size: 1.5rem;
            font-weight: bold;
          }

          .close-btn {
            font-size: 1.2rem;
            background: none;
            border: none;
            color: #333;
            cursor: pointer;
            font-weight: bold;
          }
        `}
      </style>
    </>
  );
};

export default LoanCalculator;
